import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoCircle, IcoPlus, IcoRefreshCw, IcoMail, IcoKey, IcoTrash, IcoCheck, IcoAlertTriangle, IcoClose } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';

function EmployeeProfile() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);
  const [deptLoading, setDeptLoading] = useState(null);
  const [deptMsg, setDeptMsg] = useState(null);

  const isManager = user?.role === 'manager';
  const canSeePay = isSuperAdmin() || isManager;
  const canManage = isSuperAdmin() || user?.role === 'adminassistant' || isManager;
  const canManageDepts = canManage;

  const getToken = () => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser).token : null;
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await fetch(`/api/employees/${id}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (response.ok) setEmployee(await response.json());
        else navigate('/team');
      } catch (error) {
        console.error('Error fetching employee:', error);
        navigate('/team');
      } finally {
        setLoading(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = storedUser ? JSON.parse(storedUser).token : null;
        const userData = storedUser ? JSON.parse(storedUser) : null;
        const organizationId = userData?.organizationId;
        const response = await fetch(`/api/departments?organizationId=${organizationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) setDepartments(await response.json());
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    if (id) { setLoading(true); fetchEmployee(); fetchDepartments(); }
  }, [id, navigate, location.key]);

  const handleSendInvite = async () => {
    if (!window.confirm(`Send invitation to ${employee.firstName} ${employee.lastName} at ${employee.email}?`)) return;
    setInviteLoading(true);
    try {
      const endpoint = employee.inviteToken ? '/api/invite/resend' : '/api/invite/manager';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerId: employee._id })
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ Invitation sent to ${employee.email}!`);
        const refreshed = await fetch(`/api/employees/${id}`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
        if (refreshed.ok) setEmployee(await refreshed.json());
      } else {
        alert(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      alert('❌ Error sending invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Assign to Me ──────────────────────────────────────────────────────────
  const isAlreadyAssigned = employee?.managerIds?.includes(user?.id) ||
    employee?.managerIds?.some(m => m === user?.id || m?._id === user?.id);

  const handleAssignToMe = async () => {
    setAssignLoading(true);
    setAssignMsg(null);
    try {
      const existingIds = employee.managerIds?.map(m => m?._id || m) || [];
      const updatedIds = [...new Set([...existingIds, user.id])];

      const response = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerIds: updatedIds })
      });

      if (response.ok) {
        const refreshed = await fetch(`/api/employees/${id}`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
        if (refreshed.ok) setEmployee(await refreshed.json());
        setAssignMsg({ type: 'success', text: `✅ ${employee.firstName} has been added to your team!` });
        setTimeout(() => setAssignMsg(null), 4000);
      } else {
        const data = await response.json();
        setAssignMsg({ type: 'error', text: `❌ ${data.message || 'Failed to assign employee'}` });
      }
    } catch (err) {
      setAssignMsg({ type: 'error', text: '❌ Error assigning employee' });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      setResetMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setResetLoading(true);
    setResetMsg(null);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ email: employee.email, newPassword: resetPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setResetMsg({ type: 'success', text: `✅ Password reset successfully for ${employee.firstName}!` });
        setResetPassword('');
        setTimeout(() => { setShowResetModal(false); setResetMsg(null); }, 2500);
      } else {
        setResetMsg({ type: 'error', text: `❌ ${data.message || 'Failed to reset password'}` });
      }
    } catch (err) {
      setResetMsg({ type: 'error', text: '❌ Error resetting password' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleToggleDepartment = async (deptId) => {
    setDeptLoading(deptId);
    setDeptMsg(null);
    try {
      const assignedIds = (employee.departmentIds || []).map(d => d.toString());
      const isAssigned = assignedIds.includes(deptId.toString());
      const updatedIds = isAssigned
        ? assignedIds.filter(d => d !== deptId.toString())
        : [...assignedIds, deptId];

      const response = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ departmentIds: updatedIds })
      });

      if (response.ok) {
        const refreshed = await fetch(`/api/employees/${id}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (refreshed.ok) setEmployee(await refreshed.json());
        try {
          const bc = new BroadcastChannel('dept-updates');
          bc.postMessage('department-updated');
          bc.close();
        } catch {}
        setDeptMsg({ type: 'success', text: isAssigned ? 'Department removed' : 'Department added' });
        setTimeout(() => setDeptMsg(null), 3000);
      } else {
        const data = await response.json();
        setDeptMsg({ type: 'error', text: data.message || 'Failed to update department' });
      }
    } catch {
      setDeptMsg({ type: 'error', text: 'Error updating department' });
    } finally {
      setDeptLoading(null);
    }
  };

  const handleDeactivate = async () => {
    const action = employee.active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${employee.firstName} ${employee.lastName}?`)) return;
    try {
      const res = await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (res.ok) navigate('/team');
      else {
        const data = await res.json();
        alert(`❌ ${data.message || 'Failed to deactivate employee'}`);
      }
    } catch {
      alert('❌ Error deactivating employee');
    }
  };

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('dept-updates');
      bc.onmessage = async () => {
        try {
          const res = await fetch(`/api/employees/${id}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
          });
          if (res.ok) setEmployee(await res.json());
        } catch {}
      };
    } catch {}
    return () => { try { bc?.close(); } catch {} };
  }, [id]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-content">
          <p style={{ textAlign: 'center', padding: '3rem', fontSize: '1.25rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!employee) return null;

  const isActive = employee.status === 'active' || employee.inviteAccepted;
  const hasPendingInvite = employee.status === 'pending' && employee.inviteToken;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <button
          onClick={() => navigate('/team')}
          style={{ marginBottom: '1.5rem', padding: '0.5rem 1rem', background: '#9ca3af', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}
        >
          ← Back to Team
        </button>

        {/* Employee Header */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2.5rem', fontWeight: '700', flexShrink: 0 }}>
              {employee.firstName?.[0]}{employee.lastName?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '600' }}>
                {employee.firstName} {employee.lastName}
              </h1>
              <p style={{ margin: '0.5rem 0', fontSize: '1.125rem', color: '#6b7280', textTransform: 'capitalize' }}>
                {employee.role === 'superadmin' ? 'Super Admin' : employee.role === 'adminassistant' ? 'Admin Assistant' : employee.role}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', background: employee.active ? '#dcfce7' : '#fee2e2', color: employee.active ? '#166534' : '#991b1b' }}>
                  {employee.active ? 'Active' : 'Inactive'}
                </span>
                {isActive ? (
                  <span style={{ padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', background: '#d1fae5', color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#22c55e"/> Active</span>
                ) : hasPendingInvite ? (
                  <span style={{ padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', background: '#fef3c7', color: '#92400e', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#f59e0b"/> Invite Sent</span>
                ) : (
                  <span style={{ padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', background: '#f3f4f6', color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#9ca3af"/> No Invite Sent</span>
                )}
                {/* Already assigned badge */}
                {isManager && isAlreadyAssigned && (
                  <span style={{ padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600', background: '#e0e7ff', color: '#4338ca' }}>Your Team Member</span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {/* Assign to Me — managers only, not already assigned */}
              {isManager && !isAlreadyAssigned && (
                <button
                  onClick={handleAssignToMe}
                  disabled={assignLoading}
                  style={{ padding: '0.75rem 1.5rem', background: assignLoading ? '#d1d5db' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: assignLoading ? '#9ca3af' : 'white', border: 'none', borderRadius: '8px', cursor: assignLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  {assignLoading ? 'Assigning...' : <><IcoPlus size={14}/> Assign to My Team</>}
                </button>
              )}

              {/* Send Invite — superadmin + manager */}
              {(isSuperAdmin() || isManager) && !isActive && (
                <button
                  onClick={handleSendInvite}
                  disabled={inviteLoading}
                  style={{ padding: '0.75rem 1.5rem', background: inviteLoading ? '#d1d5db' : hasPendingInvite ? '#f59e0b' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: inviteLoading ? '#9ca3af' : 'white', border: 'none', borderRadius: '8px', cursor: inviteLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  {inviteLoading ? 'Sending...' : hasPendingInvite ? <><IcoRefreshCw size={14}/> Resend Invite</> : <><IcoMail size={14}/> Send Invite</>}
                </button>
              )}

              {/* Edit — superadmin + adminassistant */}
              {canManage && (
                <button
                  onClick={() => navigate(`/employee/${id}/edit`)}
                  style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  Edit Details
                </button>
              )}
              {/* Reset Password — superadmin + manager */}
              {(isSuperAdmin() || isManager) && (
                <button
                  onClick={() => { setShowResetModal(true); setResetPassword(''); setResetMsg(null); }}
                  style={{ padding: '0.75rem 1.5rem', background: '#fff7ed', color: '#c2410c', border: '1.5px solid #fed7aa', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  <><IcoKey size={14}/> Reset Password</>
                </button>
              )}
              {/* Deactivate — superadmin + adminassistant */}
              {canManage && employee.role !== 'superadmin' && (
                <button
                  onClick={handleDeactivate}
                  style={{ padding: '0.75rem 1.5rem', background: employee.active ? '#fee2e2' : '#dcfce7', color: employee.active ? '#991b1b' : '#166534', border: `1.5px solid ${employee.active ? '#fca5a5' : '#86efac'}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  {employee.active ? <><IcoTrash size={14}/> Deactivate</> : <><IcoCheck size={14}/> Reactivate</>}
                </button>
              )}
            </div>
          </div>

          {/* Assign success/error message */}
          {assignMsg && (
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', background: assignMsg.type === 'success' ? '#d1fae5' : '#fee2e2', color: assignMsg.type === 'success' ? '#065f46' : '#991b1b' }}>
              {assignMsg.text}
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {/* Basic Information */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.25rem' }}>Basic Information</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Employee Number</label>
                <p style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{employee._id.slice(-8).toUpperCase()}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Email</label>
                <p style={{ margin: 0, fontSize: '1rem' }}>{employee.email || 'Not set'}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Start Date</label>
                <p style={{ margin: 0, fontSize: '1rem' }}>{employee.startDate ? new Date(employee.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'Not set'}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Employment Type</label>
                <p style={{ margin: 0, fontSize: '1rem' }}>
                  {employee.employmentType === 'fulltime' ? 'Full-time'
                    : employee.employmentType === 'parttime' ? 'Part-time'
                    : employee.employmentType === 'salaried' ? 'Salaried'
                    : 'Not set'}
                </p>
              </div>
            </div>
          </div>

          {/* Pay Information — superadmin only */}
          {canSeePay && (
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.25rem' }}>Pay Information</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Hourly Rate</label>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#10b981' }}>${employee.hourlyRate?.toFixed(2) || '0.00'}/hr</p>
                </div>
              </div>
            </div>
          )}

          {/* Departments */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.25rem' }}>Departments</h3>
            {canManageDepts ? (
              <div>
                {departments.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No departments created yet. Add departments in Settings.</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {departments.map(dept => {
                      const assigned = (employee.departmentIds || []).some(
                        d => d.toString() === dept._id.toString()
                      );
                      return (
                        <button
                          key={dept._id}
                          onClick={() => handleToggleDepartment(dept._id)}
                          disabled={deptLoading === dept._id}
                          style={{
                            padding: '0.5rem 1rem',
                            background: assigned ? dept.color : 'transparent',
                            color: assigned ? 'white' : dept.color,
                            border: `2px solid ${dept.color}`,
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: deptLoading === dept._id ? 'not-allowed' : 'pointer',
                            opacity: deptLoading === dept._id ? 0.6 : 1,
                            transition: 'all 0.15s'
                          }}
                        >
                          {deptLoading === dept._id ? '...' : assigned ? <><IcoCheck size={12} color="white"/> {dept.name}</> : `+ ${dept.name}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                {deptMsg && (
                  <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', fontWeight: '600', color: deptMsg.type === 'success' ? '#065f46' : '#991b1b' }}>
                    {deptMsg.text}
                  </p>
                )}
              </div>
            ) : (
              employee.departmentIds && employee.departmentIds.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {employee.departmentIds.map(deptId => {
                    const dept = departments.find(d => d._id.toString() === deptId.toString());
                    if (!dept) return null;
                    return (
                      <span key={deptId.toString()} style={{ padding: '0.5rem 1rem', background: dept.color, color: 'white', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', display: 'inline-block' }}>
                        {dept.name}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><IcoAlertTriangle size={14} color="#92400e"/> No departments assigned</p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#92400e' }}>Contact an admin to assign this employee to departments</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
      {/* Reset Password Modal */}
      {showResetModal && (
        <div onClick={() => setShowResetModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', padding: '2rem', width: '400px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Reset Password</h2>
              <button onClick={() => setShowResetModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
            </div>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
              Set a new temporary password for <strong>{employee.firstName} {employee.lastName}</strong> ({employee.email})
            </p>
            <input
              type="text"
              placeholder="Enter new password (min 6 characters)"
              value={resetPassword}
              onChange={e => setResetPassword(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem', marginBottom: '1rem', boxSizing: 'border-box', outline: 'none' }}
            />
            {resetMsg && (
              <div style={{ padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: '600', fontSize: '0.875rem', background: resetMsg.type === 'success' ? '#d1fae5' : '#fee2e2', color: resetMsg.type === 'success' ? '#065f46' : '#991b1b' }}>
                {resetMsg.text}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowResetModal(false)} style={{ flex: 1, padding: '0.75rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleResetPassword} disabled={resetLoading}
                style={{ flex: 1, padding: '0.75rem', background: resetLoading ? '#d1d5db' : '#c2410c', color: 'white', border: 'none', borderRadius: '8px', cursor: resetLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeProfile;